import { useState } from 'react';
import { useStore } from 'zustand';

import { type EntityId, inDrawOrder, kindOf } from '@/domain/model';
import { type EditorStore } from '@/store';

import { PanelSection } from './Inspector';
import { KIND_NAMES } from './kindNames';
import { colour } from './theme';

/**
 * The object list.
 *
 * ## Why it lists in reverse draw order
 *
 * Topmost first, like every layers panel the user has met elsewhere. The document stores
 * ascending z — what paints last is on top — so the list is reversed on the way out.
 * Showing the raw order would be truthful to the model and wrong for the reader.
 *
 * ## Why derived objects are shown, not hidden
 *
 * A midpoint the user never created explicitly still appears here. It is a real object
 * they can hide, lock and restyle, and hiding it from the list would make its presence in
 * the drawing inexplicable. The row says where it came from instead.
 *
 * ## Marking scaffolding
 *
 * The hollow diamond marks an object as *construction* — the helper lines a teacher wants to
 * hide once the result is on the board. It is the only signal "hide helpers" acts on, because
 * the earlier attempt to infer it was badly wrong: almost everything in a geometry drawing is
 * derived, so guessing hid the user's own segments.
 *
 * ## What the row does NOT offer
 *
 * No delete button. Deleting a parent cascades to everything built on it, and a one-click
 * cascade next to a visibility toggle is how a user loses half a construction. Deletion
 * stays on the canvas where the selection makes the blast radius visible.
 */

export interface LayersPanelProps {
  readonly store: EditorStore;
}

export function LayersPanel({ store }: LayersPanelProps) {
  const document = useStore(store, (state) => state.document);
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  const [renaming, setRenaming] = useState<EntityId | null>(null);

  // Topmost first: the list reads the way the drawing is stacked.
  const rows = [...inDrawOrder(document)].reverse();

  return (
    <PanelSection title="Đối tượng" badge={String(rows.length)} flush>
      {/*
        The list keeps a height cap of its own even inside the rail's scroll. Without one, a
        forty-entity drawing pushes every other section out of sight, and the user has to
        scroll past the whole document to reach the style controls.
      */}
      <div style={{ maxHeight: '34vh', overflowY: 'auto' }}>
        {rows.length === 0 && (
          <p style={{ margin: 0, padding: '10px', color: colour.faint, fontSize: 11 }}>
            Chưa vẽ gì.
          </p>
        )}

        {rows.map((entity, index) => {
          const selected = selection.has(entity.id);
          return (
            <div
              key={entity.id}
              className={selected ? 'es-row es-row--on' : 'es-row'}
              style={{
                gap: 4,
                padding: '3px 8px 3px 10px',
                borderRadius: 0,
                // A row of controls, not a control: the name inside it is the button.
                cursor: 'default',
              }}
            >
              <IconToggle
                on={entity.visible}
                onIcon="👁"
                offIcon="🚫"
                title={entity.visible ? 'Ẩn khỏi hình' : 'Hiện lại'}
                onClick={() => {
                  commands.setPresentation([entity.id], { visible: !entity.visible });
                }}
              />
              <IconToggle
                on={!entity.isConstruction}
                onIcon="◆"
                offIcon="◇"
                title={
                  entity.isConstruction
                    ? 'Coi là hình chính thức'
                    : 'Đánh dấu là đường dựng hình phụ'
                }
                onClick={() => {
                  commands.setPresentation([entity.id], {
                    isConstruction: !entity.isConstruction,
                  });
                }}
              />
              <IconToggle
                on={!entity.locked}
                onIcon="🔓"
                offIcon="🔒"
                title={entity.locked ? 'Mở khoá' : 'Khoá lại để khỏi chọn nhầm'}
                onClick={() => {
                  commands.setPresentation([entity.id], { locked: !entity.locked });
                }}
              />

              {renaming === entity.id ? (
                <RenameField
                  initial={entity.label ?? ''}
                  onCommit={(label) => {
                    commands.setPresentation([entity.id], {
                      label: label.trim() === '' ? null : label.trim(),
                    });
                    setRenaming(null);
                  }}
                  onCancel={() => {
                    setRenaming(null);
                  }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    commands.select([entity.id]);
                  }}
                  onDoubleClick={() => {
                    setRenaming(entity.id);
                  }}
                  title={`${KIND_NAMES[kindOf(entity.source)]} · ${entity.source.op} — bấm đúp để đổi tên`}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 5,
                    padding: '2px 3px',
                    border: 'none',
                    background: 'transparent',
                    font: 'inherit',
                    color: entity.visible ? 'inherit' : colour.faint,
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 9,
                      height: 9,
                      flex: '0 0 auto',
                      borderRadius: 3,
                      alignSelf: 'center',
                      background: entity.style.stroke,
                      // Inside rather than around, so a swatch never nudges the row's baseline.
                      boxShadow: 'inset 0 0 0 1px rgb(15 23 42 / 18%)',
                    }}
                  />
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: entity.label === undefined ? 400 : 600,
                    }}
                  >
                    {entity.label ?? KIND_NAMES[kindOf(entity.source)]}
                  </span>
                  {entity.label !== undefined && (
                    <span style={{ fontSize: 10, color: colour.faint }}>
                      {kindOf(entity.source)}
                    </span>
                  )}
                </button>
              )}

              {/*
               * Arrows point the way the row moves on screen, not the way z moves. The
               * list is reversed, so "up" is +1 in draw order.
               */}
              <OrderButton
                label="↑"
                title="Đưa lên trên — vẽ đè lên các đối tượng khác"
                disabled={index === 0}
                onClick={() => {
                  commands.moveInDrawOrder(entity.id, 1);
                }}
              />
              <OrderButton
                label="↓"
                title="Đưa xuống dưới — để đối tượng khác vẽ đè lên"
                disabled={index === rows.length - 1}
                onClick={() => {
                  commands.moveInDrawOrder(entity.id, -1);
                }}
              />
            </div>
          );
        })}
      </div>
    </PanelSection>
  );
}

function IconToggle({
  on,
  onIcon,
  offIcon,
  title,
  onClick,
}: {
  readonly on: boolean;
  readonly onIcon: string;
  readonly offIcon: string;
  readonly title: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={!on}
      onClick={onClick}
      style={{
        flex: '0 0 auto',
        width: 19,
        padding: 0,
        border: 'none',
        background: 'transparent',
        fontSize: 10,
        lineHeight: 1,
        opacity: on ? 0.75 : 1,
        cursor: 'pointer',
      }}
    >
      {on ? onIcon : offIcon}
    </button>
  );
}

function OrderButton({
  label,
  title,
  disabled,
  onClick,
}: {
  readonly label: string;
  readonly title: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        flex: '0 0 auto',
        width: 15,
        padding: 0,
        border: 'none',
        background: 'transparent',
        font: '11px/1 var(--es-font)',
        color: disabled ? colour.line : colour.muted,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

/**
 * Inline rename.
 *
 * Commits on Enter or blur and abandons on Escape — the three endings a user expects. It
 * keeps its own draft so a keystroke does not push a document edit per character, which
 * would fill the undo stack with one step per letter.
 */
function RenameField({
  initial,
  onCommit,
  onCancel,
}: {
  readonly initial: string;
  readonly onCommit: (label: string) => void;
  readonly onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);

  return (
    <input
      autoFocus
      value={draft}
      aria-label="Object name"
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onBlur={() => {
        onCommit(draft);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onCommit(draft);
        else if (event.key === 'Escape') onCancel();
        event.stopPropagation();
      }}
      style={{
        flex: 1,
        minWidth: 0,
        padding: '1px 4px',
        borderRadius: 3,
        border: '1px solid #2563eb',
        font: '12px/1.3 var(--es-font)',
      }}
    />
  );
}
